import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AppNavigation } from "../components/AppNavigation";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { SchedulerStatus } from "../components/SchedulerStatus";
import { 
  Calendar, 
  Sparkles, 
  BarChart3, 
  Image, 
  Users,
  TrendingUp,
  Clock,
  FileText,
  Plus,
  ArrowUpRight,
  Twitter,
  Instagram,
  Facebook,
  Linkedin,
  Youtube
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const DashboardPage = ({ user, logout, token }) => {
  const [analytics, setAnalytics] = useState(null);
  const [performance, setPerformance] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        
        const [analyticsRes, performanceRes, postsRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/analytics/overview`, { headers }),
          axios.get(`${BACKEND_URL}/api/analytics/performance?days=7`, { headers }),
          axios.get(`${BACKEND_URL}/api/posts?limit=5`, { headers })
        ]);
        
        setAnalytics(analyticsRes.data);
        setPerformance(performanceRes.data.performance);
        setRecentPosts(postsRes.data);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const platformIcons = {
    twitter: <Twitter className="w-4 h-4" />,
    instagram: <Instagram className="w-4 h-4" />,
    facebook: <Facebook className="w-4 h-4" />,
    linkedin: <Linkedin className="w-4 h-4" />,
    youtube: <Youtube className="w-4 h-4" />,
    tiktok: <span className="text-xs font-bold">TT</span>,
    pinterest: <span className="text-xs font-bold">P</span>
  };

  const platformColors = {
    twitter: "bg-blue-500/20 text-blue-400",
    instagram: "bg-pink-500/20 text-pink-400",
    facebook: "bg-indigo-500/20 text-indigo-400",
    linkedin: "bg-sky-500/20 text-sky-400",
    youtube: "bg-red-500/20 text-red-400",
    tiktok: "bg-gray-500/20 text-gray-400",
    pinterest: "bg-red-700/20 text-red-400"
  };

  return (
    <div className="min-h-screen bg-background">
      <AppNavigation user={user} logout={logout} />
      
      <main className="lg:ml-64 pt-14 lg:pt-0 pb-20 lg:pb-0 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
                ¡Hola, {user?.name?.split(' ')[0]}!
              </h1>
              <p className="text-muted-foreground mt-1">
                Aquí tienes el resumen de tu actividad
              </p>
            </div>
            <Link to="/composer">
              <Button className="gradient-neural glow-neural text-white" data-testid="new-post-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Post
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              {/* Stats Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <Card data-testid="stat-total-posts">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Posts Totales</p>
                        <p className="text-3xl font-bold mt-1">{analytics?.posts?.total || 0}</p>
                      </div>
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center glow-cyan">
                        <FileText className="w-6 h-6 text-primary" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card data-testid="stat-reach">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Alcance Total</p>
                        <p className="text-3xl font-bold mt-1">
                          {analytics?.engagement?.total_reach?.toLocaleString() || 0}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center glow-purple">
                        <Users className="w-6 h-6 text-accent" />
                      </div>
                    </div>
                    <div className="flex items-center gap-1 mt-2 text-sm text-primary">
                      <TrendingUp className="w-4 h-4" />
                      <span>+{analytics?.engagement?.growth || 0}%</span>
                    </div>
                  </CardContent>
                </Card>

                <Card data-testid="stat-engagement">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Interacciones</p>
                        <p className="text-3xl font-bold mt-1">
                          {analytics?.engagement?.total_interactions?.toLocaleString() || 0}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center">
                        <BarChart3 className="w-6 h-6 text-primary" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card data-testid="stat-scheduled">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Programados</p>
                        <p className="text-3xl font-bold mt-1">{analytics?.posts?.scheduled || 0}</p>
                      </div>
                      <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center">
                        <Clock className="w-6 h-6 text-accent" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Main Content Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Performance Chart */}
                <Card className="lg:col-span-8" data-testid="performance-chart">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg font-semibold">Rendimiento (7 días)</CardTitle>
                    <Link to="/analytics">
                      <Button variant="ghost" size="sm">
                        Ver más <ArrowUpRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64 min-h-[200px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={performance}>
                          <defs>
                            <linearGradient id="colorReach" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#22D3EE" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#22D3EE" stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id="colorEngagement" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#A855F7" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#A855F7" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <XAxis 
                            dataKey="date" 
                            tickFormatter={(value) => value.split('-')[2]}
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                          />
                          <YAxis 
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'hsl(var(--card))', 
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px',
                              color: 'hsl(var(--foreground))'
                            }}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="reach" 
                            stroke="#22D3EE" 
                            fillOpacity={1} 
                            fill="url(#colorReach)" 
                            name="Alcance"
                          />
                          <Area 
                            type="monotone" 
                            dataKey="engagement" 
                            stroke="#A855F7" 
                            fillOpacity={1} 
                            fill="url(#colorEngagement)" 
                            name="Engagement"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Best Times */}
                <Card className="lg:col-span-4" data-testid="best-times">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold flex items-center gap-2">
                      <Clock className="w-5 h-5 text-primary" />
                      Mejores Horarios
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {analytics?.best_times?.map((time, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{time.day}</p>
                            <p className="text-sm text-muted-foreground">{time.time}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-2 bg-secondary rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-primary rounded-full"
                                style={{ width: `${time.score}%` }}
                              ></div>
                            </div>
                            <span className="text-sm font-medium w-8">{time.score}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Platform Stats */}
                <Card className="lg:col-span-6" data-testid="platform-stats">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold">Estadísticas por Red</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      {analytics?.platform_stats && Object.entries(analytics.platform_stats).map(([platform, stats]) => (
                        <div 
                          key={platform}
                          className="p-4 bg-secondary/30 rounded-lg"
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`p-1.5 rounded ${platformColors[platform]}`}>
                              {platformIcons[platform]}
                            </span>
                            <span className="font-medium capitalize">{platform}</span>
                          </div>
                          <p className="text-2xl font-bold">{stats.followers?.toLocaleString()}</p>
                          <div className="flex items-center gap-1 mt-1 text-sm text-green-600">
                            <TrendingUp className="w-3 h-3" />
                            <span>+{stats.growth}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Recent Posts */}
                <Card className="lg:col-span-6" data-testid="recent-posts">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg font-semibold">Posts Recientes</CardTitle>
                    <Link to="/calendar">
                      <Button variant="ghost" size="sm">
                        Ver todos <ArrowUpRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  </CardHeader>
                  <CardContent>
                    {recentPosts.length === 0 ? (
                      <div className="text-center py-8">
                        <Image className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground mb-4">No tienes posts aún</p>
                        <Link to="/composer">
                          <Button variant="outline" size="sm">
                            <Plus className="w-4 h-4 mr-2" />
                            Crear tu primer post
                          </Button>
                        </Link>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {recentPosts.map((post) => (
                          <div 
                            key={post.post_id}
                            className="p-4 bg-card border border-border rounded-lg hover:border-primary/30 transition-colors"
                          >
                            <p className="text-sm line-clamp-2 mb-2">{post.content}</p>
                            <div className="flex items-center justify-between">
                              <div className="flex gap-1">
                                {post.platforms?.map((platform) => (
                                  <span 
                                    key={platform}
                                    className={`p-1 rounded text-xs ${platformColors[platform]}`}
                                  >
                                    {platformIcons[platform]}
                                  </span>
                                ))}
                              </div>
                              <span className={`text-xs px-2 py-1 rounded ${
                                post.status === 'published' ? 'bg-primary/20 text-primary' :
                                post.status === 'scheduled' ? 'bg-accent/20 text-accent' :
                                'bg-muted text-muted-foreground'
                              }`}>
                                {post.status === 'published' ? 'Publicado' :
                                 post.status === 'scheduled' ? 'Programado' : 'Borrador'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Scheduler Status */}
                <Card className="lg:col-span-6" data-testid="scheduler-widget">
                  <CardHeader className="pb-2">
                    <Link to="/calendar" className="flex items-center justify-between">
                      <CardTitle className="text-lg font-semibold flex items-center gap-2">
                        <Clock className="w-5 h-5 text-primary" />
                        Publicaciones Programadas
                      </CardTitle>
                      <Button variant="ghost" size="sm">
                        Ver más <ArrowUpRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  </CardHeader>
                  <CardContent>
                    <SchedulerStatus token={token} />
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};
